import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import {
  TrendingUp, TrendingDown, Download, Filter, Calendar, Info,
  FileText, Clock, Bookmark, Bell, ArrowRight, HelpCircle,
  CheckCircle2, Target, Activity, Zap, Flame, Heart, Shield, DollarSign, Users as UsersIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

// --- MOCK DATA ---

const engagementTrend = [
  { week: 'Apr 6', value: 55 }, { week: 'Apr 13', value: 58 }, { week: 'Apr 20', value: 52 },
  { week: 'Apr 27', value: 60 }, { week: 'May 4', value: 57 }, { week: 'May 11', value: 63 },
  { week: 'May 18', value: 62 },
];
const adherenceTrend = [
  { week: 'Apr 6', value: 48 }, { week: 'Apr 13', value: 46 }, { week: 'Apr 20', value: 50 },
  { week: 'Apr 27', value: 52 }, { week: 'May 4', value: 49 }, { week: 'May 11', value: 55 },
  { week: 'May 18', value: 54 },
];
const retentionTrend = [
  { week: 'Apr 6', value: 42 }, { week: 'Apr 13', value: 38 }, { week: 'Apr 20', value: 45 },
  { week: 'Apr 27', value: 40 }, { week: 'May 4', value: 44 }, { week: 'May 11', value: 50 },
  { week: 'May 18', value: 48 },
];

const cohortData = [
  { name: 'Seniors 65+', color: '#6b21a8', members: 812, engagement: 64, adherence: 56, highRisk: 28, trend: [60, 62, 58, 64, 61, 63, 64] },
  { name: 'Metabolic Risk', color: '#ea580c', members: 642, engagement: 58, adherence: 49, highRisk: 34, trend: [55, 52, 50, 48, 51, 53, 58] },
  { name: 'Hypertension Mgmt', color: '#ca8a04', members: 534, engagement: 61, adherence: 60, highRisk: 22, trend: [58, 60, 59, 61, 62, 60, 61] },
  { name: 'Food as Medicine Pilot', color: '#16a34a', members: 413, engagement: 71, adherence: 63, highRisk: 17, trend: [65, 67, 69, 70, 71, 72, 71] },
  { name: 'New Members <30 Days', color: '#dc2626', members: 226, engagement: 43, adherence: 35, highRisk: 24, trend: [38, 35, 40, 42, 38, 41, 43] },
];

const riskDistribution = [
  { name: 'High Risk', value: 482, pct: 19.6, color: '#dc2626' },
  { name: 'Medium Risk', value: 936, pct: 38.1, color: '#f97316' },
  { name: 'Low Risk', value: 704, pct: 28.7, color: '#22c55e' },
  { name: 'Minimal Risk', value: 336, pct: 13.6, color: '#6b21a8' },
];

const riskMovement = [
  { month: 'Apr 6', high: 22, medium: 38, low: 27, minimal: 13 },
  { month: 'Apr 13', high: 21, medium: 39, low: 27, minimal: 13 },
  { month: 'Apr 20', high: 20, medium: 38, low: 28, minimal: 14 },
  { month: 'Apr 27', high: 20, medium: 37, low: 29, minimal: 14 },
  { month: 'May 4', high: 19, medium: 38, low: 29, minimal: 14 },
  { month: 'May 12', high: 20, medium: 38, low: 29, minimal: 13 },
];

const interventions = [
  { name: 'Sleep Support Nudges', icon: '💤', sent: 12842, actionRate: 48, impact: '+18%' },
  { name: 'Nutrition Guidance', icon: '🥗', sent: 10721, actionRate: 43, impact: '+14%' },
  { name: 'Care Navigator Outreach', icon: '❤️', sent: 8765, actionRate: 51, impact: '+20%' },
  { name: 'Mindfulness / Stress Program', icon: '🧘', sent: 6192, actionRate: 47, impact: '+15%' },
];

const equityData = [
  { group: 'Overall', engagement: 62, gap: null },
  { group: 'Black / African American', engagement: 50, gap: -12 },
  { group: 'Hispanic / Latino', engagement: 54, gap: -8 },
  { group: 'Asian', engagement: 68, gap: 6 },
  { group: 'White', engagement: 66, gap: 4 },
  { group: 'Other / Multiracial', engagement: 56, gap: -6 },
];

const retentionCohorts = [
  { interval: '7-Day', rate: 82, change: '+3%' },
  { interval: '30-Day', rate: 64, change: '-2%' },
  { interval: '60-Day', rate: 51, change: '+1%' },
  { interval: '90-Day', rate: 42, change: '-4%' },
];

// Mini sparkline component
const Sparkline = ({ data, color, width = 80, height = 24 }) => {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * height}`).join(' ');
  return (
    <svg width={width} height={height} className="inline-block">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('trends');
  const [cohortFilter, setCohortFilter] = useState('all');
  const [timePeriod, setTimePeriod] = useState('30d');
  const [groupBy, setGroupBy] = useState('week');
  const [cohortMetric, setCohortMetric] = useState('adherence');

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  const tabs = [
    { id: 'trends', label: 'Trends' },
    { id: 'cohorts', label: 'Cohorts' },
    { id: 'outcomes', label: 'Outcomes' },
    { id: 'equity', label: 'Equity' },
    { id: 'interventions', label: 'Interventions' },
    { id: 'exports', label: 'Exports' },
  ];

  const totalMembers = riskDistribution.reduce((sum, r) => sum + r.value, 0);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-64"></div>
        <div className="grid grid-cols-3 gap-6">
          <div className="skeleton h-64"></div>
          <div className="skeleton h-64"></div>
          <div className="skeleton h-64"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="analytics-title">
            Analytics
          </h1>
          <p className="text-gray-500 text-sm mt-1">Deep insights to improve outcomes, close gaps, and drive measurable impact.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="text-xs gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            May 6 – May 12, 2025
          </Button>
          <Select value={cohortFilter} onValueChange={setCohortFilter}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="cohort-filter">
              <SelectValue placeholder="All Cohorts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cohorts</SelectItem>
              <SelectItem value="seniors">Seniors 65+</SelectItem>
              <SelectItem value="metabolic">Metabolic Risk</SelectItem>
              <SelectItem value="hypertension">Hypertension Mgmt</SelectItem>
              <SelectItem value="fam">Food as Medicine</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="text-xs gap-1.5">
            <Filter className="w-3.5 h-3.5" />
            Filters
          </Button>
          <Button size="sm" className="text-xs gap-1.5 bg-purple-700 hover:bg-purple-800" data-testid="export-btn">
            <Download className="w-3.5 h-3.5" />
            Export
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6" data-testid="analytics-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-700 text-purple-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ===== TRENDS TAB ===== */}
      {activeTab === 'trends' && (
        <div className="space-y-6">
          {/* Section 1: Trends Over Time */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>1. Trends Over Time</h2>
                <button className="text-gray-400 hover:text-gray-600" title="Track engagement, adherence, and retention to understand how your population is doing.">
                  <HelpCircle className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>Group by:</span>
                <Select value={groupBy} onValueChange={setGroupBy}>
                  <SelectTrigger className="w-24 h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="week">Week</SelectItem>
                    <SelectItem value="month">Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-gray-500 -mt-3 mb-4">Track engagement, adherence, and retention to understand how your population is doing.</p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Engagement Over Time */}
              <Card data-testid="engagement-trend-card">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold text-gray-900">Engagement Over Time</span>
                      <button className="text-gray-400 hover:text-gray-600" title="Weighted score: 30% check-ins, 25% weekly active use, 20% action completion, 15% trigger engagement, 10% streak consistency">
                        <HelpCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>62%</span>
                      <div className="flex items-center gap-1 text-[10px]">
                        <span className="text-gray-500">This period</span>
                        <span className="text-red-500 font-medium flex items-center"><TrendingDown className="w-3 h-3" /> 6%</span>
                        <span className="text-gray-400">vs. prior 30 days</span>
                      </div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={140}>
                    <AreaChart data={engagementTrend}>
                      <defs>
                        <linearGradient id="engGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6b21a8" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#6b21a8" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="week" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} stroke="#9CA3AF" />
                      <Tooltip />
                      <Area type="monotone" dataKey="value" stroke="#6b21a8" strokeWidth={2} fill="url(#engGrad)" dot={{ r: 3, fill: '#6b21a8' }} name="Engagement %" />
                    </AreaChart>
                  </ResponsiveContainer>
                  {/* Engagement breakdown */}
                  <div className="mt-3 pt-2 border-t border-gray-100">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1.5">Engagement Breakdown</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                      <div className="flex justify-between"><span className="text-gray-500">Check-in completion</span><span className="font-medium">30%</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Weekly return rate</span><span className="font-medium">25%</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Action completion</span><span className="font-medium">20%</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Trigger response</span><span className="font-medium">15%</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Streak consistency</span><span className="font-medium">10%</span></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Adherence Over Time */}
              <Card data-testid="adherence-trend-card">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold text-gray-900">Adherence Over Time</span>
                      <button className="text-gray-400 hover:text-gray-600" title="(Completed recommended actions / Total assigned actions) × 100">
                        <HelpCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>54%</span>
                      <div className="flex items-center gap-1 text-[10px]">
                        <span className="text-gray-500">This period</span>
                        <span className="text-red-500 font-medium flex items-center"><TrendingDown className="w-3 h-3" /> 4%</span>
                        <span className="text-gray-400">vs. prior 30 days</span>
                      </div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={140}>
                    <AreaChart data={adherenceTrend}>
                      <defs>
                        <linearGradient id="adhGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#16a34a" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="week" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} stroke="#9CA3AF" />
                      <Tooltip />
                      <Area type="monotone" dataKey="value" stroke="#16a34a" strokeWidth={2} fill="url(#adhGrad)" dot={{ r: 3, fill: '#16a34a' }} name="Adherence %" />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div className="mt-3 pt-2 border-t border-gray-100">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Formula</p>
                    <p className="text-[11px] text-gray-500">(Completed actions &divide; Total assigned) &times; 100</p>
                  </div>
                </CardContent>
              </Card>

              {/* Retention Over Time */}
              <Card data-testid="retention-trend-card">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold text-gray-900">Retention Over Time</span>
                      <span className="text-[10px] text-gray-400">(Returning Users)</span>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>48%</span>
                      <div className="flex items-center gap-1 text-[10px]">
                        <span className="text-gray-500">This period</span>
                        <span className="text-red-500 font-medium flex items-center"><TrendingDown className="w-3 h-3" /> 8%</span>
                        <span className="text-gray-400">vs. prior 30 days</span>
                      </div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={140}>
                    <AreaChart data={retentionTrend}>
                      <defs>
                        <linearGradient id="retGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#9333ea" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#9333ea" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="week" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} stroke="#9CA3AF" />
                      <Tooltip />
                      <Area type="monotone" dataKey="value" stroke="#9333ea" strokeWidth={2} fill="url(#retGrad)" dot={{ r: 3, fill: '#9333ea' }} name="Retention %" />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div className="mt-3 pt-2 border-t border-gray-100">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1.5">Retention Intervals</p>
                    <div className="flex items-center gap-3 text-[11px]">
                      {retentionCohorts.map((r) => (
                        <div key={r.interval} className="text-center">
                          <p className="font-semibold text-gray-900">{r.rate}%</p>
                          <p className="text-gray-400">{r.interval}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Section 2: Cohort Performance + Section 3: Risk Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cohort Performance */}
            <Card data-testid="cohort-performance-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>2. Cohort Performance</CardTitle>
                    <button className="text-gray-400 hover:text-gray-600"><HelpCircle className="w-4 h-4" /></button>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <span>Metric:</span>
                    <Select value={cohortMetric} onValueChange={setCohortMetric}>
                      <SelectTrigger className="w-28 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="adherence">Adherence</SelectItem>
                        <SelectItem value="engagement">Engagement</SelectItem>
                        <SelectItem value="highRisk">High Risk %</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Compare key metrics across cohorts to identify top performers and at-risk groups.</p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 border-b border-gray-100">
                        <th className="text-left py-2 font-medium">Cohort</th>
                        <th className="text-right py-2 font-medium">Members</th>
                        <th className="text-right py-2 font-medium">Engagement</th>
                        <th className="text-right py-2 font-medium">Adherence</th>
                        <th className="text-right py-2 font-medium">High Risk %</th>
                        <th className="text-right py-2 font-medium">Trend (30 Days)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cohortData.map((cohort) => (
                        <tr key={cohort.name} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: cohort.color }} />
                              <span className="font-medium text-gray-900">{cohort.name}</span>
                            </div>
                          </td>
                          <td className="text-right py-2.5 text-gray-700">{cohort.members.toLocaleString()}</td>
                          <td className="text-right py-2.5">
                            <span className={cohort.engagement >= 65 ? 'text-green-700 font-medium' : cohort.engagement >= 50 ? 'text-gray-700' : 'text-red-600 font-medium'}>
                              {cohort.engagement}%
                            </span>
                          </td>
                          <td className="text-right py-2.5">
                            <span className={cohort.adherence >= 60 ? 'text-green-700 font-medium' : cohort.adherence >= 45 ? 'text-gray-700' : 'text-red-600 font-medium'}>
                              {cohort.adherence}%
                            </span>
                          </td>
                          <td className="text-right py-2.5">
                            <span className={cohort.highRisk >= 30 ? 'text-red-600 font-medium' : 'text-gray-700'}>
                              {cohort.highRisk}%
                            </span>
                          </td>
                          <td className="text-right py-2.5">
                            <Sparkline data={cohort.trend} color={cohort.trend[6] >= cohort.trend[0] ? '#16a34a' : '#dc2626'} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="mt-3 text-xs text-purple-700 font-medium hover:underline flex items-center gap-1">
                  View all cohorts <ArrowRight className="w-3 h-3" />
                </button>
              </CardContent>
            </Card>

            {/* Risk Distribution */}
            <Card data-testid="risk-distribution-card">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>3. Risk Distribution</CardTitle>
                  <button className="text-gray-400 hover:text-gray-600"><HelpCircle className="w-4 h-4" /></button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Understand the makeup of your population and how risk is changing.</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {/* Donut */}
                  <div>
                    <p className="text-xs font-medium text-gray-700 mb-2">Current Risk Level</p>
                    <div className="relative">
                      <ResponsiveContainer width="100%" height={160}>
                        <PieChart>
                          <Pie data={riskDistribution} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={2}>
                            {riskDistribution.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                          </Pie>
                          <Tooltip formatter={(v) => v.toLocaleString()} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center">
                          <p className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{totalMembers.toLocaleString()}</p>
                          <p className="text-[10px] text-gray-500">Members</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1 mt-1">
                      {riskDistribution.map((r) => (
                        <div key={r.name} className="flex items-center justify-between text-[11px]">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: r.color }} />
                            <span className="text-gray-600">{r.name}</span>
                          </div>
                          <span className="text-gray-700 font-medium">{r.value.toLocaleString()} ({r.pct}%)</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Risk Movement */}
                  <div>
                    <p className="text-xs font-medium text-gray-700 mb-2">Risk Movement (30 Days)</p>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={riskMovement} stackOffset="expand">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="month" tick={{ fontSize: 9 }} stroke="#9CA3AF" />
                        <YAxis tickFormatter={(v) => `${Math.round(v * 100)}%`} tick={{ fontSize: 9 }} stroke="#9CA3AF" />
                        <Tooltip formatter={(v) => `${v}%`} />
                        <Bar dataKey="high" stackId="a" fill="#dc2626" name="High Risk" />
                        <Bar dataKey="medium" stackId="a" fill="#f97316" name="Medium Risk" />
                        <Bar dataKey="low" stackId="a" fill="#22c55e" name="Low Risk" />
                        <Bar dataKey="minimal" stackId="a" fill="#6b21a8" name="Minimal Risk" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                    <button className="mt-2 text-xs text-purple-700 font-medium hover:underline flex items-center gap-1">
                      View risk movement report <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Section 4 + 5 + 6 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Intervention Effectiveness */}
            <Card data-testid="interventions-card">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>4. Intervention Effectiveness</CardTitle>
                  <button className="text-gray-400 hover:text-gray-600"><HelpCircle className="w-4 h-4" /></button>
                </div>
                <p className="text-xs text-gray-500 mt-1">See which interventions and nudges are driving the best results.</p>
              </CardHeader>
              <CardContent>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-100">
                      <th className="text-left py-1.5 font-medium">Intervention / Nudge</th>
                      <th className="text-right py-1.5 font-medium">Sent</th>
                      <th className="text-right py-1.5 font-medium">Action Rate</th>
                      <th className="text-right py-1.5 font-medium">Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interventions.map((item) => (
                      <tr key={item.name} className="border-b border-gray-50">
                        <td className="py-2 font-medium text-gray-900">
                          <span className="mr-1.5">{item.icon}</span>{item.name}
                        </td>
                        <td className="text-right py-2 text-gray-700">{item.sent.toLocaleString()}</td>
                        <td className="text-right py-2 text-gray-700">{item.actionRate}%</td>
                        <td className="text-right py-2 text-green-700 font-medium">{item.impact}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button className="mt-3 text-xs text-purple-700 font-medium hover:underline flex items-center gap-1">
                  View all interventions <ArrowRight className="w-3 h-3" />
                </button>
              </CardContent>
            </Card>

            {/* Equity & Gap Analysis */}
            <Card data-testid="equity-card">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>5. Equity & Gap Analysis</CardTitle>
                  <button className="text-gray-400 hover:text-gray-600"><HelpCircle className="w-4 h-4" /></button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Track disparities to ensure equitable engagement and outcomes.</p>
              </CardHeader>
              <CardContent>
                <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Engagement by Race / Ethnicity</p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-100">
                      <th className="text-left py-1.5 font-medium">Group</th>
                      <th className="text-left py-1.5 font-medium w-32">Engagement</th>
                      <th className="text-right py-1.5 font-medium">Gap vs Overall</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equityData.map((item) => (
                      <tr key={item.group} className="border-b border-gray-50">
                        <td className="py-2 font-medium text-gray-900 text-[11px]">{item.group}</td>
                        <td className="py-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-purple-600" style={{ width: `${item.engagement}%` }} />
                            </div>
                            <span className="text-[11px] text-gray-700 font-medium w-8">{item.engagement}%</span>
                          </div>
                        </td>
                        <td className="text-right py-2">
                          {item.gap !== null ? (
                            <span className={`text-[11px] font-medium ${item.gap >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                              {item.gap > 0 ? '+' : ''}{item.gap}%
                            </span>
                          ) : (
                            <span className="text-[11px] text-gray-400">&mdash;</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button className="mt-3 text-xs text-purple-700 font-medium hover:underline flex items-center gap-1">
                  View full equity report <ArrowRight className="w-3 h-3" />
                </button>
              </CardContent>
            </Card>

            {/* Outcomes & Impact */}
            <Card data-testid="outcomes-card">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>6. Outcomes & Impact</CardTitle>
                  <button className="text-gray-400 hover:text-gray-600"><HelpCircle className="w-4 h-4" /></button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Monitor program impact and estimated value.</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-4 h-4 text-green-600" />
                      <p className="text-[10px] text-gray-500 uppercase font-medium">Est. Risk Reduction</p>
                    </div>
                    <p className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>18%</p>
                    <p className="text-[10px] text-gray-500">vs. prior 30 days <span className="text-green-600 font-medium ml-1">+4%</span></p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-blue-600" />
                      <p className="text-[10px] text-gray-500 uppercase font-medium">Est. Avoidable Events</p>
                    </div>
                    <p className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>126</p>
                    <p className="text-[10px] text-gray-500">vs. prior 30 days <span className="text-green-600 font-medium ml-1">+21</span></p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex items-center gap-2 mb-1">
                      <Activity className="w-4 h-4 text-purple-600" />
                      <p className="text-[10px] text-gray-500 uppercase font-medium">Est. Cost Savings</p>
                    </div>
                    <p className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>$1.24M</p>
                    <p className="text-[10px] text-gray-500">vs. prior 30 days <span className="text-green-600 font-medium ml-1">+$210K</span></p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex items-center gap-2 mb-1">
                      <UsersIcon className="w-4 h-4 text-orange-600" />
                      <p className="text-[10px] text-gray-500 uppercase font-medium">Active Impacted</p>
                    </div>
                    <p className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>2,458</p>
                    <p className="text-[10px] text-gray-500">of 3,120 total (79%)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Section 7: Reporting & Exports */}
          <Card data-testid="reporting-card">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>7. Reporting & Exports</CardTitle>
                <button className="text-gray-400 hover:text-gray-600"><HelpCircle className="w-4 h-4" /></button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => toast.info('Export report coming soon')}>
                  <FileText className="w-3.5 h-3.5" />
                  Export Report
                </Button>
                <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => toast.info('Schedule report coming soon')}>
                  <Clock className="w-3.5 h-3.5" />
                  Schedule Report
                </Button>
                <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => toast.info('View saved')}>
                  <Bookmark className="w-3.5 h-3.5" />
                  Save View
                </Button>
                <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => toast.info('Alert created')}>
                  <Bell className="w-3.5 h-3.5" />
                  Create Alert
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== COHORTS TAB ===== */}
      {activeTab === 'cohorts' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Cohort Deep Dive</CardTitle>
            <p className="text-xs text-gray-500">Detailed cohort-level analysis and comparison tools.</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-500 border-b border-gray-200">
                    <th className="text-left py-2 font-medium">Cohort</th>
                    <th className="text-right py-2 font-medium">Members</th>
                    <th className="text-right py-2 font-medium">Engagement</th>
                    <th className="text-right py-2 font-medium">Adherence</th>
                    <th className="text-right py-2 font-medium">High Risk %</th>
                    <th className="text-right py-2 font-medium">30-Day Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {cohortData.map((cohort) => (
                    <tr key={cohort.name} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer">
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cohort.color }} />
                          <span className="font-medium">{cohort.name}</span>
                        </div>
                      </td>
                      <td className="text-right py-3">{cohort.members.toLocaleString()}</td>
                      <td className="text-right py-3">{cohort.engagement}%</td>
                      <td className="text-right py-3">{cohort.adherence}%</td>
                      <td className="text-right py-3">
                        <span className={cohort.highRisk >= 30 ? 'text-red-600 font-medium' : ''}>{cohort.highRisk}%</span>
                      </td>
                      <td className="text-right py-3">
                        <Sparkline data={cohort.trend} color={cohort.trend[6] >= cohort.trend[0] ? '#16a34a' : '#dc2626'} width={100} height={28} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== OUTCOMES TAB ===== */}
      {activeTab === 'outcomes' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-5">
              <Shield className="w-6 h-6 text-green-600 mb-2" />
              <p className="text-xs text-gray-500 uppercase font-medium">Est. Risk Reduction</p>
              <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>18%</p>
              <p className="text-xs text-gray-500 mt-1">vs. prior 30 days <span className="text-green-600 font-medium">+4%</span></p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5">
              <DollarSign className="w-6 h-6 text-blue-600 mb-2" />
              <p className="text-xs text-gray-500 uppercase font-medium">Est. Avoidable Events</p>
              <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>126</p>
              <p className="text-xs text-gray-500 mt-1">vs. prior 30 days <span className="text-green-600 font-medium">+21</span></p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5">
              <Activity className="w-6 h-6 text-purple-600 mb-2" />
              <p className="text-xs text-gray-500 uppercase font-medium">Est. Cost Savings</p>
              <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>$1.24M</p>
              <p className="text-xs text-gray-500 mt-1">vs. prior 30 days <span className="text-green-600 font-medium">+$210K</span></p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5">
              <UsersIcon className="w-6 h-6 text-orange-600 mb-2" />
              <p className="text-xs text-gray-500 uppercase font-medium">Active Members Impacted</p>
              <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>2,458</p>
              <p className="text-xs text-gray-500 mt-1">of 3,120 total (79%)</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== EQUITY TAB ===== */}
      {activeTab === 'equity' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Equity & Gap Analysis</CardTitle>
            <p className="text-xs text-gray-500">Track disparities to ensure equitable engagement and outcomes.</p>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b border-gray-200">
                  <th className="text-left py-2 font-medium">Group</th>
                  <th className="text-left py-2 font-medium w-48">Engagement</th>
                  <th className="text-right py-2 font-medium">Gap vs Overall</th>
                </tr>
              </thead>
              <tbody>
                {equityData.map((item) => (
                  <tr key={item.group} className="border-b border-gray-100">
                    <td className="py-3 font-medium">{item.group}</td>
                    <td className="py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-purple-600" style={{ width: `${item.engagement}%` }} />
                        </div>
                        <span className="text-sm font-medium w-10">{item.engagement}%</span>
                      </div>
                    </td>
                    <td className="text-right py-3">
                      {item.gap !== null ? (
                        <span className={`font-medium ${item.gap >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                          {item.gap > 0 ? '+' : ''}{item.gap}%
                        </span>
                      ) : <span className="text-gray-400">&mdash;</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ===== INTERVENTIONS TAB ===== */}
      {activeTab === 'interventions' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Intervention Effectiveness</CardTitle>
            <p className="text-xs text-gray-500">See which interventions and nudges are driving the best results.</p>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b border-gray-200">
                  <th className="text-left py-2 font-medium">Intervention / Nudge</th>
                  <th className="text-right py-2 font-medium">Sent</th>
                  <th className="text-right py-2 font-medium">Action Rate</th>
                  <th className="text-right py-2 font-medium">Impact on Adherence</th>
                </tr>
              </thead>
              <tbody>
                {interventions.map((item) => (
                  <tr key={item.name} className="border-b border-gray-100">
                    <td className="py-3 font-medium"><span className="mr-2">{item.icon}</span>{item.name}</td>
                    <td className="text-right py-3">{item.sent.toLocaleString()}</td>
                    <td className="text-right py-3">{item.actionRate}%</td>
                    <td className="text-right py-3 text-green-700 font-medium">{item.impact}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ===== EXPORTS TAB ===== */}
      {activeTab === 'exports' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Reporting & Exports</CardTitle>
            <p className="text-xs text-gray-500">Generate and schedule reports for your organization.</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button className="p-6 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-center" onClick={() => toast.info('Export coming soon')}>
                <FileText className="w-6 h-6 mx-auto mb-2 text-purple-700" />
                <p className="text-sm font-medium">Export Report</p>
              </button>
              <button className="p-6 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-center" onClick={() => toast.info('Schedule coming soon')}>
                <Clock className="w-6 h-6 mx-auto mb-2 text-purple-700" />
                <p className="text-sm font-medium">Schedule Report</p>
              </button>
              <button className="p-6 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-center" onClick={() => toast.info('View saved')}>
                <Bookmark className="w-6 h-6 mx-auto mb-2 text-purple-700" />
                <p className="text-sm font-medium">Save View</p>
              </button>
              <button className="p-6 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-center" onClick={() => toast.info('Alert created')}>
                <Bell className="w-6 h-6 mx-auto mb-2 text-purple-700" />
                <p className="text-sm font-medium">Create Alert</p>
              </button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Analytics;
